import moment from "moment";
import { hasValue } from "~/helpers/common-helpers";
import { getUpcomingTenant, type ExternalEntity, type Tenancy } from "~/helpers/entities/tenancy-helpers";
import type { TenantStatus } from "~/helpers/modules/rent-roll-helpers";

export type ArrearsBalance = {
  balance?: Nullable<number>;
  currency?: Nullable<string>;
};

export type Tenant = {
  id: string;
  name?: Nullable<string>;
  currency?: Nullable<string>;
  moveInDate?: Nullable<string>;
  evictionDate?: Nullable<string>;
  latestPlannedRentMetric?: Nullable<RentMetric>;
  balanceAt?: Nullable<ArrearsBalance>;
  latestBalance?: Nullable<ArrearsBalance>;
  irrevocableUntilLandlord?: Nullable<string>;
  irrevocableUntilTenant?: Nullable<string>;
  isGhost?: Nullable<boolean>;
  regulation?: Nullable<string>;
} & ExternalEntity;

export type RentMetric = {
  currency?: Nullable<string>;
  amount?: Nullable<number>;
};
export type Tenants = Tenant[];

export const isTenantCurrent = (tenant: Tenant) => {
  if (!tenant) return false;

  const today = moment();
  const moveInDate = moment(tenant.moveInDate);
  const evictionDate = moment(tenant.evictionDate);

  if (moveInDate.isAfter(today)) return false;
  if (evictionDate.isBefore(today)) return false;

  return true;
};

export const getRemainingTenants = (currentTenant: Nullable<Tenant>, tenants: Tenants) => {
  if (tenants == null || tenants.length === 0) return [];

  const sorted = [...tenants].sort((a, b) => {
    return isUnikId(getExternalIdForTenant(a)) && isUnikId(getExternalIdForTenant(b)) // Special case for UNIK data as we can use their Id to determine the hostory
      ? unikIdSort(a, b)
      : moment(b.moveInDate).diff(moment(a.moveInDate));
  });

  if (currentTenant == null) return sorted;

  return sorted.filter((tenant) => {
    return tenant.id !== currentTenant.id;
  });
};

export const getExternalIdForTenant = (tenant: ExternalEntity) => {
  const externalId = tenant.assetManagementExternalIDs?.items.find((item) => {
    return item.externalIDType == "id";
  });

  return externalId ? externalId.externalID : null;
};

export const isUnikId = (id: string | null) => {
  if (id === null) return false;

  const regex = new RegExp("([0-9]*[-]){3}[0-9*]");

  return regex.test(id);
};

export const unikIdSort = function (a: Tenant, b: Tenant) {
  const aId = getExternalIdForTenant(a);
  const bId = getExternalIdForTenant(b);

  if (aId == null || bId == null) return 0;

  const tentantNumberA = parseInt(aId.split("-")[3]);
  const tentantNumberB = parseInt(bId.split("-")[3]);

  return tentantNumberB - tentantNumberA;
};

export const getYearlyRentForTenant = function (tenant: Tenant) {
  return tenant?.latestPlannedRentMetric?.amount || 0;
};

export const getMonthlyRentForTenant = function (tenant: Tenant) {
  const yearly = getYearlyRentForTenant(tenant);

  return hasValue(yearly) ? yearly / 12 : 0;
};

export const getTenantStatus = (row: Tenancy): TenantStatus => (!!hasValue(getUpcomingTenant(row)) ? "upcomingTenant" : "noUpcomingTenant");
